import { useRef } from "react";
import { useDispatch } from "react-redux";

import {
  useFetchAdminAreaHierarchy,
  useFetchAdminAreasByIds
} from "@/api/adminArea/userAdminAreaAPI";
import { optimizeSelectValue } from "@/api/configuration/useConfigurationAPI";
import { useFetchCountries } from "@/api/country/useCountryAPI";
import { findNodeByCode } from "@/helpers/datasetAttributeHelper";
import { useGlobalAdminAreaSync } from "@/hooks/globalAdminArea/useGlobalFilterAdminArea";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  setGlobalAdminArea,
  setSelectedAreaIds
} from "@/store/slice/globalFilterSlice";
import type {
  AdministrativeArea,
  AdministrativeAreaExtract
} from "@/types/adminArea";

import type {
  AnalysisConfiguration,
  GlobalAdmArea
} from "@lokasi-ai-web-sdk/agent-ui";

import { hasBoundary } from "./utils";

/**
 * Both halves are compared: an area the agent sent without a usable id is still
 * a distinct area, so keying on `adm_area_id` alone would collide them all.
 */
const getAreaIdentity = (area: GlobalAdmArea) =>
  `${area.adm_area_id}|${area.full_code}`;

/** One identity for the whole set, so a reordered set is not a new set. */
const getAreasIdentity = (areas: readonly GlobalAdmArea[]) =>
  areas
    .map(getAreaIdentity)
    .sort((a, b) => a.localeCompare(b))
    .join(" ");

/**
 * The SDK never validates the configuration document field by field — and its
 * type still names this one singular — so the shape is checked here.
 */
const readWorkingAreas = (
  configuration: AnalysisConfiguration | null
): readonly GlobalAdmArea[] => {
  const value = configuration?.global_adm_areas;
  return Array.isArray(value) ? (value as readonly GlobalAdmArea[]) : [];
};

/**
 * Draws the working area the agent settled on, and keeps the map framed on it.
 *
 * The "resolve an id, store it, draw it, fit the camera" path already exists for
 * the global filter, so this only resolves the reference and hands it over.
 *
 * It tracks the set of areas it applied, because the `globalFilter` slice is
 * shared with Spatial Analysis: a workspace that never had an area must leave
 * the user's own selection alone.
 */
export const useAgentGlobalAdmArea = () => {
  const dispatch = useDispatch();

  /** Identity of the area set this hook last applied, or null for none. */
  const appliedAreaIdentityRef = useRef<string | null>(null);
  const { isAdminTreeEnabled } = useAppFeatures();

  const { syncAdminAreaToGlobalFilter } = useGlobalAdminAreaSync();
  const fetchAdminAreasByIds = useFetchAdminAreasByIds();
  const fetchHierarchy = useFetchAdminAreaHierarchy();
  // Fetched on demand, not on render: a render-time observer is still empty on a
  // cold load, which is exactly when the first workspace state arrives.
  const fetchCountries = useFetchCountries();

  /**
   * Last resort when the agent sends a code but no id. Codes travel dotted
   * (`"36.74"`) while the hierarchy stores them plain (`"3674"`), so both
   * spellings are tried.
   */
  const resolveAreaIdByFullCode = async (fullCode: string) => {
    if (!fullCode) return null;

    const codeCandidates = [
      ...new Set([fullCode, fullCode.replace(/\./g, "")])
    ];

    const countries = await fetchCountries({ subscribed: true }).catch(
      () => []
    );

    for (const country of countries.filter(
      (item) => item.id && item.name.toLowerCase() !== "global"
    )) {
      const hierarchy = await fetchHierarchy(country.id).catch(() => []);

      for (const code of codeCandidates) {
        const node = findNodeByCode(code, hierarchy);
        if (node) return node.id;
      }
    }

    return null;
  };

  /**
   * Takes back this hook's own area. Never the user's: the ledger is only
   * non-null once this hook applied one.
   */
  const clearAppliedGlobalAdmArea = () => {
    dispatch(setGlobalAdminArea(null));
    dispatch(setSelectedAreaIds([]));
    appliedAreaIdentityRef.current = null;
  };

  /** The hierarchy lookups behind the code path are cached, so these overlap. */
  const resolveAreaId = async (area: GlobalAdmArea) =>
    area.adm_area_id?.trim()
      ? area.adm_area_id.trim()
      : await resolveAreaIdByFullCode(area.full_code);

  const syncGlobalAdmArea = async (
    configuration: AnalysisConfiguration | null,
    signal: AbortSignal
  ) => {
    const areas = readWorkingAreas(configuration);
    const areasIdentity = areas.length > 0 ? getAreasIdentity(areas) : null;

    // Already on the map. Returning here keeps every delta of a run — and a
    // clone carrying the same areas — from refetching and yanking the camera
    // away from a user who is panning.
    if (areasIdentity === appliedAreaIdentityRef.current) return;

    if (areasIdentity === null) {
      clearAppliedGlobalAdmArea();
      return;
    }

    const areaIds = (await Promise.all(areas.map(resolveAreaId))).filter(
      (id): id is string => Boolean(id)
    );

    if (signal.aborted) return;

    if (areaIds.length === 0) {
      console.warn("Agent working areas resolved to no area id", { areas });
      return;
    }

    const areaDetails = await fetchAdminAreasByIds(areaIds);

    if (signal.aborted) return;

    // One area losing its shape must not cost the others theirs.
    const drawableAreas = areaDetails.filter(
      (area): area is AdministrativeArea => hasBoundary(area)
    );

    if (drawableAreas.length === 0) {
      console.warn("Agent working areas have no boundary to draw", { areaIds });
      return;
    }

    // Every level, because `setAdminAreaSettings` merges — a partial value would
    // leave a stale deeper level behind. The first area fills it: the settings
    // hold one value per level, so they cannot carry two areas of equal rank.
    await syncAdminAreaToGlobalFilter({
      adminArea: optimizeSelectValue(
        drawableAreas[0] as AdministrativeAreaExtract
      ),
      // Drawn areas only, so `selectedAreaIds` never claims one that fell out.
      adminAreaIds: drawableAreas.map((area) => area.id),
      isAdminTreeEnabled
    });

    appliedAreaIdentityRef.current = areasIdentity;
  };

  return { syncGlobalAdmArea, clearAppliedGlobalAdmArea };
};
